import React from 'react';
import { shallowEqual } from 'react-redux';

import { useAppSelector } from 'src/hooks/useStore';
import { useStudioRootRedirect } from 'src/hooks/useStudioRootRedirect';
import { HomePageContent } from 'src/legacy/components/HomePage';
import { NotAllowedPage } from 'src/legacy/components/UI/NotAllowedPage';
import { LoadingPage } from 'src/legacy/components/UI/Pages/LoadingPage';
import { NetworkErrorPage } from 'src/legacy/components/UI/Pages/NetworkErrorPage';
import type { RouteAccess } from 'src/routes/routeAccess';
import { useRouteAccess } from 'src/routes/routeAccess';
import { useGetPortalConfigQuery } from 'src/services/api';
import { RootState } from 'src/store';

export interface AccessGateProps {
  access?: keyof RouteAccess;
  unauthAccess?: boolean;
  children: React.ReactNode;
}

export const AccessGate: React.FC<AccessGateProps> = ({
  access,
  unauthAccess = false,
  children,
}) => {
  const { isLoading: isPortalConfigurationLoading } = useGetPortalConfigQuery();

  const { routeAccess, isLoading: isAccessLoading } = useRouteAccess();

  const { loadingError, permissions, userLoaded } = useAppSelector(
    (state: RootState) => ({
      loadingError: state.user.loadingError,
      permissions: state.user.data?.permissions,
      userLoaded: state.user.loaded,
    }),
    shallowEqual,
  );

  const { shouldShowHomePage, isRedirecting } = useStudioRootRedirect();

  const allowed = access ? routeAccess[access] : undefined;

  // Priority 1: Network/loading error
  if (loadingError) {
    return <NetworkErrorPage />;
  }

  // Priority 2: Access not allowed (only for authenticated users with loaded permissions)
  if (!allowed && !isAccessLoading && userLoaded && access !== undefined) {
    return <NotAllowedPage />;
  }

  // Priority 3: Loading states
  if (
    isPortalConfigurationLoading ||
    isAccessLoading ||
    (!permissions && !unauthAccess)
  ) {
    return <LoadingPage />;
  }

  // Priority 4: Studio users skip Dashboard on home paths; Classic users see Home.
  if (isRedirecting) {
    return <LoadingPage />;
  }

  if (shouldShowHomePage) {
    return <HomePageContent />;
  }

  return <>{children}</>;
};
